/**
 * Komal OS - Vendor Directory: scan a business card (standalone).
 *
 * The gap this fills: VendorDetailPage.js's own scan button only ever
 * attaches a contact to the ONE vendor you're already viewing. This page
 * is for the actual "I just met someone new at a fair, no vendor record
 * for them yet" case - scan first, review, then either let it
 * auto-match/create a vendor by the company name on the card, or search
 * for and pick a different existing vendor to attach to instead.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Camera, UploadCloud, Loader2, CheckCircle2, AlertTriangle, RotateCcw, Clock, ArrowLeft, Search, X, PenLine,
} from 'lucide-react';
import { scanVendorCard, getVendorScanJob, confirmVendorCard, listVendors } from '../../services/vendorService';
import useUIStore from '../../store/uiStore';

const FIELD_LABELS = { name: 'Name', title: 'Title', company: 'Company', phone: 'Phone', email: 'Email' };
const FIELD_ORDER = ['name', 'title', 'company', 'phone', 'email'];
const blankFields = () => ({ name: '', title: '', company: '', phone: '', email: '' });

export default function VendorCardScanPage() {
  const { showToast } = useUIStore();

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [job, setJob] = useState(null);

  const [fields, setFields] = useState(blankFields());
  const [confidence, setConfidence] = useState({});
  const [handwrittenNote, setHandwrittenNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Which vendor this contact will attach to - null means "auto-match/
  // create by the company name typed above" (the default, matching the
  // CRM scanner's own find-or-create behavior).
  const [pickedVendor, setPickedVendor] = useState(null);
  const [vendorQuery, setVendorQuery] = useState('');
  const [vendorResults, setVendorResults] = useState([]);

  const fileInputRef = useRef(null);
  const pollTimer = useRef(null);

  useEffect(() => () => clearTimeout(pollTimer.current), []);

  useEffect(() => {
    if (!vendorQuery.trim()) { setVendorResults([]); return undefined; }
    const timer = setTimeout(() => {
      listVendors({ q: vendorQuery }).then((res) => setVendorResults(res.data.vendors)).catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [vendorQuery]);

  const resetScan = () => {
    setFile(null);
    setPreviewUrl(null);
    setJob(null);
    setFields(blankFields());
    setConfidence({});
    setHandwrittenNote('');
    setPickedVendor(null);
    setVendorQuery('');
    setVendorResults([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChosen = (chosen) => {
    if (!chosen) return;
    setFile(chosen);
    setPreviewUrl(URL.createObjectURL(chosen));
    setJob(null);
  };

  const applyResult = (doneJob) => {
    const r = doneJob.result || {};
    setFields({ name: r.name || '', title: r.title || '', company: r.company || '', phone: r.phone || '', email: r.email || '' });
    setConfidence(r.confidence || {});
    setHandwrittenNote(r.handwritten_note || '');
  };

  const pollJob = (jobId) => {
    pollTimer.current = setTimeout(async () => {
      try {
        const res = await getVendorScanJob(jobId);
        setJob(res.data.job);
        if (res.data.job.status === 'done') applyResult(res.data.job);
        else if (res.data.job.status === 'failed') showToast(res.data.job.error || 'Card extraction failed', 'error');
        else pollJob(jobId);
      } catch {
        pollJob(jobId);
      }
    }, 5000);
  };

  const startScan = async () => {
    if (!file) return;
    setScanning(true);
    try {
      const res = await scanVendorCard(file);
      setJob(res.data.job);
      if (res.data.job.status === 'done') applyResult(res.data.job);
      else if (res.data.job.status === 'failed') showToast(res.data.job.error || 'Card extraction failed', 'error');
      else pollJob(res.data.job.id);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not scan the card', 'error');
    } finally {
      setScanning(false);
    }
  };

  const submitConfirm = async () => {
    if (!fields.name.trim() || !fields.company.trim()) {
      showToast('Name and company are required', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await confirmVendorCard({
        job_id: job.id, ...fields, vendor_id: pickedVendor ? pickedVendor.id : undefined,
        handwritten_note: handwrittenNote.trim() || null,
      });
      showToast(`Saved ${res.data.contact.name} at ${res.data.vendor.name}`, 'success');
      resetScan();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not save this contact', 'error');
    } finally {
      setSaving(false);
    }
  };

  const isBusy = job && ['pending', 'processing', 'queued'].includes(job.status);
  const isDone = job && job.status === 'done';

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <Link to="/vendors" className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white mb-3">
          <ArrowLeft size={13} /> Vendors
        </Link>
        <div className="mb-6">
          <h1 className="text-xl font-bold text-white">Scan a vendor's card</h1>
          <p className="text-sm text-white/70 mt-0.5">Photo in, review the extraction, then save — matches an existing vendor by company name, or creates a new one.</p>
        </div>

        {!job && (
          <div className="glass-card p-5 mb-5">
            {previewUrl ? (
              <div className="mb-4">
                <img src={previewUrl} alt="Card preview" className="w-full max-h-80 object-contain rounded-lg border border-gray-200" />
              </div>
            ) : (
              <label
                htmlFor="vendor-card-upload"
                className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-komal-blue rounded-xl py-14 cursor-pointer transition-colors"
              >
                <Camera size={30} className="text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">Take a photo or upload a business card</span>
                <span className="text-xs text-gray-400 mt-1">Works from your phone camera at a vendor fair, or upload later</span>
              </label>
            )}
            <input
              id="vendor-card-upload" ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => handleFileChosen(e.target.files?.[0])}
            />
            <div className="flex gap-2 mt-4">
              {previewUrl && (
                <>
                  <label htmlFor="vendor-card-upload" className="text-sm font-medium text-gray-600 border border-gray-300 rounded px-3 py-2 cursor-pointer hover:bg-gray-50 flex items-center gap-1.5">
                    <UploadCloud size={14} /> Choose a different photo
                  </label>
                  <button
                    onClick={startScan} disabled={scanning}
                    className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold text-white bg-komal-blue rounded px-3 py-2 disabled:opacity-60"
                  >
                    {scanning ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
                    {scanning ? 'Scanning…' : 'Scan card'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {isBusy && (
          <div className="glass-card p-5 mb-5 flex items-center gap-3">
            <Loader2 size={18} className="animate-spin text-komal-blue shrink-0" />
            <div className="text-sm">
              {job.status === 'queued' ? (
                <>
                  <div className="font-semibold text-gray-800">Provider is busy — queued{job.queue_position ? ` (position ${job.queue_position})` : ''}</div>
                  <div className="text-gray-500">
                    <Clock size={12} className="inline -mt-0.5 mr-1" />
                    Next attempt in about {job.eta_seconds ? `${job.eta_seconds}s` : 'a minute'}. This page keeps checking.
                  </div>
                </>
              ) : (
                <div className="font-semibold text-gray-800">Scanning card…</div>
              )}
            </div>
          </div>
        )}

        {isDone && (
          <div className="glass-card p-5">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 size={16} className="text-green-600" />
              <span className="text-sm font-semibold text-gray-800">Review before saving</span>
            </div>

            <div className="grid gap-3 mb-4">
              {FIELD_ORDER.map((f) => {
                const isLow = confidence[f] === 'low';
                return (
                  <div key={f}>
                    <label className="flex items-center gap-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {FIELD_LABELS[f]}
                      {isLow && (
                        <span className="flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-[10px] font-bold normal-case tracking-normal">
                          <AlertTriangle size={10} /> check this
                        </span>
                      )}
                    </label>
                    <input
                      value={fields[f]}
                      onChange={(e) => setFields((prev) => ({ ...prev, [f]: e.target.value }))}
                      disabled={f === 'company' && !!pickedVendor}
                      className={`w-full border rounded px-3 py-2 text-sm ${isLow ? 'border-amber-300 bg-amber-50' : 'border-gray-300'} ${f === 'company' && pickedVendor ? 'bg-gray-100 text-gray-400' : ''}`}
                    />
                  </div>
                );
              })}
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                <PenLine size={12} /> Handwritten note (if anything's written on the card)
              </label>
              <textarea
                value={handwrittenNote}
                onChange={(e) => setHandwrittenNote(e.target.value)}
                placeholder="e.g. Met at Elecrama, stall 12"
                rows={2}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>

            <div className="border-t border-gray-100 pt-4 mb-4">
              {pickedVendor ? (
                <div className="flex items-center justify-between bg-komal-blue/5 border border-komal-blue/20 rounded-lg px-3 py-2">
                  <div className="text-sm">
                    <span className="text-gray-500">Attaching to existing vendor: </span>
                    <span className="font-semibold text-komal-blue">{pickedVendor.name}</span>
                  </div>
                  <button onClick={() => setPickedVendor(null)} className="text-gray-400 hover:text-gray-600"><X size={15} /></button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-gray-500 mb-1.5">
                    Will create/match a vendor named <span className="font-semibold text-gray-700">"{fields.company || '—'}"</span> automatically. Search below to attach to a different existing vendor instead.
                  </p>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      value={vendorQuery} onChange={(e) => setVendorQuery(e.target.value)}
                      placeholder="Search existing vendors…"
                      className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-300"
                    />
                    {vendorResults.length > 0 && (
                      <div className="absolute z-10 w-full bg-white border border-gray-200 rounded shadow-lg mt-1 max-h-48 overflow-y-auto">
                        {vendorResults.map((v) => (
                          <button
                            key={v.id} type="button"
                            onClick={() => { setPickedVendor(v); setVendorQuery(''); setVendorResults([]); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                          >
                            {v.name} {v.category && <span className="text-gray-400">· {v.category}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-2">
              <button onClick={resetScan} className="flex items-center gap-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded px-3 py-2 hover:bg-gray-50">
                <RotateCcw size={14} /> Discard
              </button>
              <button
                onClick={submitConfirm} disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold text-white bg-green-700 rounded px-3 py-2 disabled:opacity-60"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                Confirm &amp; save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
